// @ts-nocheck

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartPie,
  faCircleCheck,
  faCircleExclamation,
  faClock,
  faRotateLeft,
  faXmark,
  faLock,
  faLockOpen,
  faWandMagicSparkles,
  faPen,
} from '@fortawesome/pro-thin-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import Modal from '@/components/Modal';
import Button, { ButtonType } from '@/components/Button';
import DollarInput from '@/components/DollarInput';
import Text from '@/components/Text';
import AssetImage from '@/components/AssetImage';
import useTradingAccount from '@/hooks/useTradingAccount';
import useCurrentUser from '@/hooks/useCurrentUser';
import CopyTradingService from '@/lib/CopyTrading';
import MarketService from '@/lib/Market';
import StringUtils from '@/utils/StringUtils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AssetService from '@/lib/Asset';
import Image from 'next/image';

export interface PortfolioStock {
  ticker: string;
  percentage: number; // 0-100 (UI display format)
  name?: string;
}

export interface PortfolioInvestModalProps {
  open: boolean;
  closeModal: () => void;
  stocks: PortfolioStock[];
  portfolioName: string;
  portfolioDescription: string;
  portfolioImage?: any; // Optional personality avatar image (Next.js static import)
}

type InvestStep = 'input' | 'loading' | 'success' | 'scheduled' | 'error';

export default function PortfolioInvestModal({
  open,
  closeModal,
  stocks,
  portfolioName,
  portfolioDescription,
  portfolioImage,
}: PortfolioInvestModalProps) {
  const [amount, setAmount] = useState(0);
  const [resetDollarInput, setResetDollarInput] = useState<number>(0);
  const [step, setStep] = useState<InvestStep>('input');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Editable breakdown state — mutable copy of stocks prop
  const [editableBreakdown, setEditableBreakdown] = useState<{ ticker: string; percentage: number }[]>([]);
  const [lockedStocks, setLockedStocks] = useState<string[]>([]);
  const [editingStock, setEditingStock] = useState<{ ticker: string; percentage: number } | null>(null);
  const [editPercentageValue, setEditPercentageValue] = useState('');
  const percentageInputRef = useRef<HTMLInputElement>(null);

  const { data: tradingAccount } = useTradingAccount();
  const { data: user } = useCurrentUser();
  const queryClient = useQueryClient();

  const buyingPower = Number(tradingAccount?.buying_power || 0);
  const preselectedAmounts = [10, 25, 50, 100].filter((n) => n <= buyingPower);

  // Sync editable breakdown from incoming stocks prop when modal opens
  // Round all percentages to 1 decimal place and adjust the largest stock
  // so the total is exactly 100% (avoids backend "sum is not 100" errors)
  useEffect(() => {
    if (open && stocks.length > 0) {
      const rounded = stocks
        .map((s) => ({ ticker: s.ticker, percentage: Math.round(s.percentage * 10) / 10 }))
        .sort((a, b) => b.percentage - a.percentage);
      const sum = rounded.reduce((acc, s) => acc + s.percentage, 0);
      const diff = Math.round((100 - sum) * 10) / 10;
      if (diff !== 0 && rounded.length > 0) {
        rounded[0].percentage = Math.round((rounded[0].percentage + diff) * 10) / 10;
      }
      setEditableBreakdown(rounded);
      setLockedStocks([]);
      setEditingStock(null);
    }
  }, [open, stocks]);

  // Fetch asset info for all stocks to display names/images
  const tickers = useMemo(() => stocks.map((s) => s.ticker), [stocks]);
  const { data: batchInfo } = useQuery({
    queryKey: ['batchAssetInfo', tickers],
    queryFn: async () => {
      const results: Record<string, any> = {};
      await Promise.allSettled(
        tickers.map(async (ticker) => {
          try {
            const info = await AssetService.getAssetInfo(ticker);
            results[ticker] = info;
          } catch {
            results[ticker] = null;
          }
        })
      );
      return results;
    },
    enabled: open && tickers.length > 0,
    staleTime: 10 * 60 * 1000,
  });

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      const timeout = setTimeout(() => {
        setAmount(0);
        setResetDollarInput(0);
        setStep('input');
        setErrorMessage('');
        setIsSubmitting(false);
        setEditingStock(null);
        setEditPercentageValue('');
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [open]);

  // Computed values from editable breakdown
  // Round the sum to 1 decimal to avoid floating point drift (e.g. 99.99999999)
  const totalWeight = useMemo(
    () => Math.round(editableBreakdown.reduce((acc, { percentage }) => acc + percentage, 0) * 10) / 10,
    [editableBreakdown]
  );
  const isBalanced = totalWeight === 100;

  const notEnoughBuyingPower = amount > buyingPower;
  const isValidAmount = amount >= 1 && !notEnoughBuyingPower;

  // Always compute the display list from editableBreakdown (visible even at $0)
  const displayBreakdown = useMemo(() => {
    return editableBreakdown.map((stock) => ({
      ticker: stock.ticker,
      name: batchInfo?.[stock.ticker]?.name || stocks.find((s) => s.ticker === stock.ticker)?.name || stock.ticker,
      percentage: stock.percentage,
      dollarAmount: amount > 0 ? (stock.percentage / 100) * amount : 0,
    }));
  }, [editableBreakdown, batchInfo, stocks, amount]);

  // Focus percentage input when editing popover opens
  useEffect(() => {
    if (editingStock) {
      setTimeout(() => percentageInputRef.current?.focus(), 100);
    }
  }, [editingStock]);

  // --- Percentage editing ---
  const onStartEdit = (ticker: string, percentage: number) => {
    setEditingStock({ ticker, percentage });
    // Show rounded value (1 decimal) so user sees the same number as the list
    setEditPercentageValue((Math.round(percentage * 10) / 10).toString());
  };

  const onPercentageInputChange = (value: string) => {
    // Validation: mirroring the sister app's PercentageInput
    if (value === '0.') return; // Don't allow 0. as input
    if (value.endsWith('.') && value.substring(0, value.length - 1).includes('.')) return; // No double decimals
    if (value !== '' && isNaN(Number(value))) return; // Must be numeric
    if (value.includes('.') && value.split('.')[1].length > 1) return; // Max 1 decimal place
    if (value.length > 5) return; // Reasonable max length
    setEditPercentageValue(value);
  };

  const onSavePercentage = () => {
    if (!editingStock) return;
    const raw = editPercentageValue ? Math.min(parseFloat(editPercentageValue), 100) : 0;
    // Round to 1 decimal to stay consistent with display
    const newPct = Math.round(raw * 10) / 10;
    setEditableBreakdown((prev) =>
      prev.map((b) =>
        b.ticker === editingStock.ticker ? { ...b, percentage: newPct } : b
      )
    );
    setEditingStock(null);
    setEditPercentageValue('');
  };

  // --- Lock/Unlock ---
  const toggleLock = (ticker: string) => {
    setLockedStocks((prev) =>
      prev.includes(ticker) ? prev.filter((s) => s !== ticker) : [...prev, ticker]
    );
  };

  // --- Rebalance (mirrors sister app logic) ---
  // All arithmetic is rounded to 1 decimal to match display and avoid backend "not 100%" errors
  const round1 = (n: number) => Math.round(n * 10) / 10;

  const onRebalance = useCallback(() => {
    let filtered = editableBreakdown.map((a) => ({ ...a, percentage: round1(a.percentage) }));

    let sum = round1(filtered.reduce((acc, asset) => acc + asset.percentage, 0));

    if (sum !== 100) {
      let difference = round1(sum - 100);

      if (difference > 0) {
        // Over 100: reduce from the last unlocked stocks
        for (let i = filtered.length - 1; i >= 0 && difference > 0; i--) {
          if (lockedStocks.includes(filtered[i].ticker)) continue;
          const reduction = round1(Math.min(difference, filtered[i].percentage));
          filtered[i].percentage = round1(filtered[i].percentage - reduction);
          difference = round1(difference - reduction);
        }
      } else {
        // Under 100: add the remainder to the last unlocked stock
        const lastUnlockedIdx = [...filtered].reverse().findIndex(
          (a) => !lockedStocks.includes(a.ticker)
        );
        if (lastUnlockedIdx !== -1) {
          const actualIdx = filtered.length - 1 - lastUnlockedIdx;
          filtered[actualIdx].percentage = round1(filtered[actualIdx].percentage + Math.abs(difference));
        }
      }

      // Filter out any stocks that hit 0%
      filtered = filtered.filter((asset) => asset.percentage > 0);

      // Final check — if still not exactly 100%, adjust the largest unlocked stock
      const finalSum = round1(filtered.reduce((acc, asset) => acc + asset.percentage, 0));
      if (filtered.length > 0 && finalSum !== 100) {
        const diff = round1(100 - finalSum);
        const lastUnlockedIdx = [...filtered].reverse().findIndex(
          (a) => !lockedStocks.includes(a.ticker)
        );
        if (lastUnlockedIdx !== -1) {
          const actualIdx = filtered.length - 1 - lastUnlockedIdx;
          filtered[actualIdx].percentage = round1(filtered[actualIdx].percentage + diff);
        }
      }
    }

    // Sort by percentage descending
    filtered.sort((a, b) => b.percentage - a.percentage);

    setEditableBreakdown(filtered);
  }, [editableBreakdown, lockedStocks]);

  const onSubmit = async () => {
    if (!isValidAmount || isSubmitting || !isBalanced) return;

    setIsSubmitting(true);
    setStep('loading');
    setErrorMessage('');

    try {
      // Backend validates sum === 100 with strict equality.
      // Send the exact display values the user sees. Compute the last stock's
      // percentage as (100 - sum_of_others) so the backend's reduce is guaranteed
      // to produce exactly 100.
      const sorted = [...editableBreakdown]
        .map((s) => ({ ticker: s.ticker, percentage: round1(s.percentage) }))
        .filter((s) => s.percentage > 0)
        .sort((a, b) => b.percentage - a.percentage);

      // Compute running sum of all except last, then set last = 100 - that sum
      if (sorted.length > 1) {
        let sumOfOthers = 0;
        for (let i = 0; i < sorted.length - 1; i++) {
          sumOfOthers += sorted[i].percentage;
        }
        sorted[sorted.length - 1].percentage = 100 - sumOfOthers;
      }
      const assets = sorted;

      const portfolio = await CopyTradingService.createPortfolio({
        name: portfolioName,
        description: portfolioDescription,
        type: 'HedgeFund',
        yearlyAvgReturn: 0,
        lastYearReturn: 0,
        minimumInvestment: 1,
        tradeDelay: 0,
        assets,
      });

      const amountInCents = Math.round(amount * 100);
      const result = await CopyTradingService.investInPortfolio(
        portfolio.id,
        amountInCents
      );

      const marketOpen = await MarketService.isMarketOpen();

      if (result.status === 'Executed') {
        setStep('success');
      } else if (result.status === 'Scheduled') {
        setStep('scheduled');
      } else {
        setStep('error');
        setErrorMessage('Investment failed. Please try again.');
      }

      queryClient.invalidateQueries(['user']);
      queryClient.invalidateQueries(['positions']);
      queryClient.invalidateQueries(['tradingAccount']);
      queryClient.invalidateQueries(['portfolioGraphData']);
      setTimeout(() => {
        queryClient.invalidateQueries(['orders']);
        queryClient.invalidateQueries(['pendingOrders']);
      }, 2000);
    } catch (error: any) {
      console.error('[PortfolioInvestModal] Error:', error);
      setStep('error');
      setErrorMessage(
        error?.response?.data?.message ||
        error?.message ||
        'Unable to place portfolio order at this time.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setEditingStock(null);
    closeModal();
  };

  const renderInputStep = () => (
    <>
      {/* Portfolio Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="size-14 rounded-2xl bg-teal-200/10 border border-teal-200/20 flex items-center justify-center flex-none overflow-hidden">
          {portfolioImage ? (
            <Image src={portfolioImage} alt={portfolioName} width={56} height={56} className="size-14 object-contain" />
          ) : (
            <FontAwesomeIcon icon={faChartPie as IconProp} className="size-6 text-teal-200" />
          )}
        </div>
        <div className="flex flex-col gap-0.5 min-w-0">
          <Text size="lg" className="font-bold text-white uppercase tracking-tight leading-tight truncate">
            {portfolioName}
          </Text>
          <Text size="sm" className="text-teal-200 font-bold uppercase tracking-widest">
            {editableBreakdown.length} Stocks
          </Text>
        </div>
      </div>

      {/* Dollar Input */}
      <div className="py-6">
        <DollarInput
          initialAmount={amount}
          resetAmount={resetDollarInput}
          onChangeAmount={(value) => setAmount(value ? parseFloat(value) : 0)}
        />
      </div>

      {/* Buying Power */}
      <Text size="sm" className="text-center text-white/40 font-medium mb-2">
        Buying power: {StringUtils.USD(buyingPower)}
      </Text>

      {notEnoughBuyingPower && (
        <Text size="sm" className="text-center text-red-400 font-bold mb-2">
          Not enough buying power
        </Text>
      )}

      {/* Preset Amounts */}
      {preselectedAmounts.length > 0 && (
        <div className="flex gap-2 justify-center mb-8">
          {preselectedAmounts.map((amt) => (
            <button
              key={amt}
              onClick={() => {
                setAmount(amt);
                setResetDollarInput(amt);
              }}
              className={`px-4 py-2 rounded-full text-sm font-bold uppercase tracking-widest transition-all border ${amt === amount
                ? 'bg-teal-200 text-black border-teal-200'
                : 'bg-white/5 text-white/60 border-white/10 hover:bg-white/10'
                }`}
            >
              {StringUtils.USD(amt)}
            </button>
          ))}
        </div>
      )}

      {/* Stock Breakdown — Always visible */}
      {displayBreakdown.length > 0 && (
        <div className="flex flex-col gap-3 mb-6 relative">
          <div className="flex items-center justify-between px-1">
            <Text size="xs" className="text-white/40 uppercase tracking-widest font-bold">
              Portfolio Breakdown
            </Text>
            {!isBalanced && (
              <Text size="xs" className="text-rose-400 font-bold uppercase tracking-widest">
                {totalWeight.toFixed(1)}% / 100%
              </Text>
            )}
          </div>
          <div className="bg-black/30 rounded-2xl border border-white/5 max-h-[240px] overflow-y-auto scrollbar-apple">
            {displayBreakdown.map((item, i) => {
              const isLocked = lockedStocks.includes(item.ticker);
              return (
                <div
                  key={item.ticker}
                  className={`flex items-center justify-between px-4 py-3 group/row hover:bg-white/5 transition-colors cursor-pointer ${i < displayBreakdown.length - 1 ? 'border-b border-white/5' : ''
                    }`}
                  onClick={() => onStartEdit(item.ticker, item.percentage)}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <AssetImage
                      alt={item.ticker}
                      src={batchInfo?.[item.ticker]?.image || ''}
                      className="size-8 rounded-lg"
                    />
                    <div className="flex flex-col min-w-0">
                      <Text size="sm" className="font-bold text-white truncate">
                        {item.name}
                      </Text>
                      <Text size="xs" className="text-white/40 font-medium">
                        {item.ticker}
                      </Text>
                    </div>
                  </div>

                  {/* Percentage bar + value */}
                  <div className="flex items-center gap-3 flex-none">
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex items-center gap-2">
                        <Text size="sm" className="font-bold text-white">
                          {item.percentage.toFixed(1)}%
                        </Text>
                        <FontAwesomeIcon
                          icon={faPen as IconProp}
                          className="size-2.5 text-white/20 group-hover/row:text-teal-200 transition-colors"
                        />
                      </div>
                      {amount > 0 && (
                        <Text size="xs" className="text-teal-200/60 font-medium">
                          {StringUtils.USD(item.dollarAmount)}
                        </Text>
                      )}
                    </div>

                    {/* Lock toggle */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleLock(item.ticker);
                      }}
                      className={`size-7 rounded-lg flex items-center justify-center transition-all ${isLocked
                        ? 'bg-rose-500/20 text-rose-400'
                        : 'bg-white/5 text-white/20 hover:text-white/40'
                        }`}
                      title={isLocked ? 'Unlock (include in rebalance)' : 'Lock (exclude from rebalance)'}
                    >
                      <FontAwesomeIcon
                        icon={(isLocked ? faLock : faLockOpen) as IconProp}
                        className="size-3"
                      />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Total row */}
          <div className="flex justify-between px-4 py-2">
            <Text size="sm" className="text-white/60 font-bold uppercase tracking-widest">Total</Text>
            <div className="flex items-center gap-4">
              <Text size="sm" className={`font-bold ${isBalanced ? 'text-white' : 'text-rose-400'}`}>
                {totalWeight.toFixed(1)}%
              </Text>
              {amount > 0 && (
                <Text size="sm" className="text-white font-bold">{StringUtils.USD(amount)}</Text>
              )}
            </div>
          </div>

          {/* Inline Percentage Edit Popover */}
          {editingStock && (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 backdrop-blur-sm rounded-2xl"
              onClick={(e) => {
                // Close popover if clicking outside the inner card
                if (e.target === e.currentTarget) {
                  setEditingStock(null);
                  setEditPercentageValue('');
                }
              }}
            >
              <div className="bg-[#1a1a2e] border border-white/10 rounded-3xl p-6 w-[280px] flex flex-col items-center gap-4 shadow-2xl">
                <div className="flex items-center gap-3">
                  <AssetImage
                    alt={editingStock.ticker}
                    src={batchInfo?.[editingStock.ticker]?.image || ''}
                    className="size-8 rounded-lg"
                  />
                  <Text size="base" className="font-bold text-white uppercase tracking-tight">
                    {editingStock.ticker}
                  </Text>
                </div>

                <Text size="xs" className="text-white/40 font-medium text-center">
                  Set how much this stock contributes. Total should be 100%.
                </Text>

                {/* Percentage Input */}
                <div className="flex items-center justify-center gap-1 my-2">
                  <input
                    ref={percentageInputRef}
                    type="text"
                    inputMode="decimal"
                    value={editPercentageValue}
                    onChange={(e) => onPercentageInputChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onSavePercentage();
                      if (e.key === 'Escape') {
                        setEditingStock(null);
                        setEditPercentageValue('');
                      }
                    }}
                    placeholder="0"
                    maxLength={5}
                    className="w-20 text-4xl font-bold text-white text-center bg-transparent border-none outline-none caret-teal-200 placeholder:text-white/20"
                  />
                  <span className={`text-2xl font-medium ml-1 ${editPercentageValue ? 'text-white' : 'text-white/40'}`}>
                    %
                  </span>
                </div>

                <div className="flex gap-2 w-full">
                  <button
                    onClick={() => {
                      setEditingStock(null);
                      setEditPercentageValue('');
                    }}
                    className="flex-1 h-11 rounded-xl bg-white/5 text-white/60 font-bold uppercase tracking-widest text-xs hover:bg-white/10 transition-all border border-white/5"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={onSavePercentage}
                    className="flex-1 h-11 rounded-xl bg-teal-200 text-black font-bold uppercase tracking-widest text-xs hover:bg-teal-300 transition-all"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer: Rebalance or Invest button */}
      {!isBalanced ? (
        <Button
          type={ButtonType.Primary}
          className="w-full h-14 rounded-2xl font-bold uppercase tracking-widest bg-rose-500 text-white hover:bg-rose-600 gap-3"
          onClick={onRebalance}
        >
          <FontAwesomeIcon icon={faWandMagicSparkles as IconProp} className="size-5" />
          {totalWeight < 100
            ? `${(100 - totalWeight).toFixed(0)}% Unallocated — Rebalance`
            : `${(totalWeight - 100).toFixed(0)}% Over — Rebalance`}
        </Button>
      ) : (
        <Button
          type={ButtonType.Primary}
          className="w-full h-14 rounded-2xl font-bold uppercase tracking-widest bg-teal-200 text-black hover:bg-teal-300"
          disabled={!isValidAmount}
          onClick={onSubmit}
        >
          Invest {amount >= 1 ? StringUtils.USD(amount) : ''}
        </Button>
      )}
    </>
  );

  const renderLoadingStep = () => (
    <div className="flex flex-col items-center justify-center gap-6 py-12">
      <div className="size-20 rounded-full bg-teal-200/10 flex items-center justify-center">
        <FontAwesomeIcon
          icon={faRotateLeft as IconProp}
          className="size-10 text-teal-200 animate-spin"
        />
      </div>
      <div className="flex flex-col items-center gap-2">
        <Text size="lg" className="font-bold text-white">
          Placing Portfolio Order...
        </Text>
        <Text size="sm" className="text-white/40 text-center max-w-xs">
          Splitting {StringUtils.USD(amount)} across {editableBreakdown.length} stocks proportionally
        </Text>
      </div>
    </div>
  );

  const renderReceiptBreakdown = () => (
    <div className="flex flex-col gap-3 w-full">
      <Text size="xs" className="text-white/40 uppercase tracking-widest font-bold px-1">
        Order Breakdown
      </Text>
      <div className="bg-black/30 rounded-2xl border border-white/5 max-h-[200px] overflow-y-auto scrollbar-apple">
        {displayBreakdown.map((item, i) => (
          <div
            key={item.ticker}
            className={`flex items-center justify-between px-4 py-2.5 ${i < displayBreakdown.length - 1 ? 'border-b border-white/5' : ''
              }`}
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <AssetImage
                alt={item.ticker}
                src={batchInfo?.[item.ticker]?.image || ''}
                className="size-7 rounded-lg"
              />
              <div className="flex flex-col min-w-0">
                <Text size="sm" className="font-bold text-white truncate">
                  {item.name}
                </Text>
                <Text size="xs" className="text-white/40 font-medium">
                  {item.ticker} · {item.percentage.toFixed(1)}%
                </Text>
              </div>
            </div>
            <Text size="sm" className="font-bold text-teal-200 flex-none ml-3">
              {StringUtils.USD(item.dollarAmount)}
            </Text>
          </div>
        ))}
      </div>
      <div className="flex justify-between px-4 py-1">
        <Text size="sm" className="text-white/60 font-bold uppercase tracking-widest">Total</Text>
        <Text size="sm" className="text-white font-bold">{StringUtils.USD(amount)}</Text>
      </div>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="flex flex-col items-center gap-6 py-6">
      <div className="size-16 rounded-full bg-green-500/10 flex items-center justify-center">
        <FontAwesomeIcon
          icon={faCircleCheck as IconProp}
          className="size-8 text-green-400"
        />
      </div>
      <div className="flex flex-col items-center gap-1">
        <Text size="lg" className="font-bold text-white">
          Portfolio Invested!
        </Text>
        <Text size="sm" className="text-white/40 text-center max-w-xs">
          {StringUtils.USD(amount)} invested in {portfolioName}
        </Text>
      </div>

      {renderReceiptBreakdown()}

      <Button
        type={ButtonType.Primary}
        className="w-full h-14 rounded-2xl font-bold uppercase tracking-widest bg-teal-200 text-black hover:bg-teal-300 mt-2"
        onClick={handleClose}
      >
        Done
      </Button>
    </div>
  );

  const renderScheduledStep = () => (
    <div className="flex flex-col items-center gap-6 py-6">
      <div className="size-16 rounded-full bg-yellow-500/10 flex items-center justify-center">
        <FontAwesomeIcon
          icon={faClock as IconProp}
          className="size-8 text-yellow-400"
        />
      </div>
      <div className="flex flex-col items-center gap-1">
        <Text size="lg" className="font-bold text-white">
          Investment Scheduled
        </Text>
        <Text size="sm" className="text-white/40 text-center max-w-xs">
          {StringUtils.USD(amount)} in {portfolioName} will be executed when the market opens
        </Text>
      </div>

      {renderReceiptBreakdown()}

      <Button
        type={ButtonType.Primary}
        className="w-full h-14 rounded-2xl font-bold uppercase tracking-widest bg-teal-200 text-black hover:bg-teal-300 mt-2"
        onClick={handleClose}
      >
        Got it
      </Button>
    </div>
  );

  const renderErrorStep = () => (
    <div className="flex flex-col items-center justify-center gap-6 py-12">
      <div className="size-20 rounded-full bg-red-500/10 flex items-center justify-center">
        <FontAwesomeIcon
          icon={faCircleExclamation as IconProp}
          className="size-10 text-red-400"
        />
      </div>
      <div className="flex flex-col items-center gap-2">
        <Text size="lg" className="font-bold text-white">
          Investment Failed
        </Text>
        <Text size="sm" className="text-white/40 text-center max-w-xs">
          {errorMessage}
        </Text>
      </div>
      <div className="flex gap-3 w-full mt-4">
        <Button
          type={ButtonType.Secondary}
          className="flex-1 h-14 rounded-2xl font-bold uppercase tracking-widest"
          onClick={handleClose}
        >
          Close
        </Button>
        <Button
          type={ButtonType.Primary}
          className="flex-1 h-14 rounded-2xl font-bold uppercase tracking-widest bg-teal-200 text-black hover:bg-teal-300"
          onClick={() => {
            setStep('input');
            setErrorMessage('');
          }}
        >
          Try Again
        </Button>
      </div>
    </div>
  );

  return (
    <Modal open={open} closeModal={handleClose}>
      <div className="bg-sharp-darkglass w-full max-w-lg rounded-[2.5rem] overflow-hidden flex flex-col max-h-[90vh] shadow-2xl border border-white/10">
        {/* Header Bar */}
        <div className="flex items-center justify-between p-6 pb-0">
          <Text size="xs" className="text-teal-200 uppercase tracking-[0.2em] font-black opacity-60">
            Portfolio Investment
          </Text>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="size-8 rounded-full bg-white/5 flex items-center justify-center text-white/40 hover:bg-white/10 hover:text-white transition-all disabled:opacity-30"
          >
            <FontAwesomeIcon icon={faXmark as IconProp} className="size-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto scrollbar-apple">
          {step === 'input' && renderInputStep()}
          {step === 'loading' && renderLoadingStep()}
          {step === 'success' && renderSuccessStep()}
          {step === 'scheduled' && renderScheduledStep()}
          {step === 'error' && renderErrorStep()}
        </div>
      </div>
    </Modal>
  );
}
